// src/hooks/useMaterialFilters.js

import { useEffect, useState } from "react";
import config from "../config";

/**
 * useMaterialFilters
 *
 * Purpose:
 *   Fetches filter metadata for the Material Management UI:
 *     - materialTypes
 *     - materialCategories
 *     - materialCategoryMap
 *
 * Backend endpoint:
 *   GET `${config.apiUrl}/material_management/filters`
 *
 */

function getCsrfToken() {
  // Meta tag approach
  const meta = typeof document !== "undefined" && document.querySelector('meta[name="csrf-token"]');
  const metaToken = meta?.getAttribute("content");
  if (metaToken) return metaToken;

  const match =
    typeof document !== "undefined" &&
    document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith("csrf_token="));

  if (match) {
    const value = match.substring("csrf_token=".length);
    return decodeURIComponent(value);
  }

  return "";
}

export const useMaterialFilters = () => {
  // -------------------------
  // State: filter metadata returned by the backend
  // -------------------------
  const [materialTypes, setMaterialTypes] = useState([]);
  const [materialCategories, setMaterialCategories] = useState([]);

  /**
   * materialCategoryMap:
   *   expected shape: { [categoryNameOrId]: Array<materialTypeOrId> | ... }
   *
   */
  const [materialCategoryMap, setMaterialCategoryMap] = useState({});

  // -------------------------
  // State: network/UI status
  // -------------------------
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    /**
     * AbortController provides a standard way to cancel fetch requests.
     * This prevents state updates after unmount and avoids the "cancelled flag" pattern.
     */
    const controller = new AbortController();

    const fetchFilters = async () => {
      setLoading(true);
      setErrorMessage("");

      // Endpoint URL is fixed; no user-controlled query params here.
      const url = `${config.apiUrl}/material_management/filters`;

      try {
        /**
         * Cookie-based auth only:
         * - We do NOT read tokens from localStorage.
         * - We do NOT set Authorization: Bearer ...
         *
         * This eliminates the biggest client-side auth footgun (XSS stealing localStorage tokens)
         * and avoids mixed-auth ambiguity (cookies + bearer on the same request).
         */
        const csrfToken = getCsrfToken();

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",

            ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
          },

          credentials: "include",

          /**
           * Allows cancellation on unmount.
           */
          signal: controller.signal,
        });

        /**
         * Attempt to parse JSON response.
         * If the server returns HTML (proxy error) or invalid JSON, this throws and is caught below.
         */
        const data = await response.json();

        /**
         * Handle non-OK responses:
         * - show user-safe errors in UI
         * - log details for developers (but avoid leaking internals to users)
         */
        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("Unauthorized. Please sign in again.");
          }

          // Developer-facing detail
          console.error("Filters endpoint returned error:", {
            status: response.status,
            statusText: response.statusText,
            body: data,
          });

          // User-facing generic message
          throw new Error("Unable to load material filters. Please try again later.");
        }

        // -------------------------
        // Normalize and validate response shapes before storing
        // -------------------------

        /**
         * materialTypes:
         * Expected an array; otherwise default to [].
         */
        setMaterialTypes(Array.isArray(data?.materialTypes) ? data.materialTypes : []);

        /**
         * materialCategories:
         * Expected an array; otherwise default to [].
         */
        setMaterialCategories(Array.isArray(data?.materialCategories) ? data.materialCategories : []);

        /**
         * materialCategoryMap:
         * Expected a plain object.
         *
         * Note: `typeof null === "object"` so we must ensure it's truthy as well.
         * Also ensure it's not an array.
         */
        const mapCandidate = data?.materialCategoryMap;
        const safeMap =
          mapCandidate &&
          typeof mapCandidate === "object" &&
          !Array.isArray(mapCandidate)
            ? mapCandidate
            : {};

        setMaterialCategoryMap(safeMap);

        /**
         * Avoid logging the full data payload in production.
         * If needed, log minimal safe info (counts) behind a dev flag.
         */
        // console.debug("Loaded material filters counts:", {
        //   types: Array.isArray(data?.materialTypes) ? data.materialTypes.length : 0,
        //   categories: Array.isArray(data?.materialCategories) ? data.materialCategories.length : 0,
        // });

      } catch (err) {
        /**
         * AbortError happens during cleanup; it is not a user-visible failure.
         */
        if (err?.name === "AbortError") return;

        // Developer-facing detail
        console.error("Error loading material filters:", err);

        /**
         * User-facing message:
         * Prefer generic messaging to avoid leaking internal server details.
         */
        setErrorMessage("Unable to load material filters. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchFilters();

    // Cleanup: cancel in-flight request on unmount.
    return () => {
      controller.abort();
    };
  }, []);

  // Return the filter metadata plus loading/error state for UI.
  return {
    materialTypes,
    materialCategories,
    materialCategoryMap,
    loading,
    errorMessage,
  };
};